/**
 * Enterprise Design System Tokens
 * Centralized theme configuration for AgileBrain Hub
 */
export const COLORS = {
  // Primary Brand Colors
  primary: {
    DEFAULT: '#1E90FF',
    50: '#E6F3FF',
    100: '#CCE7FF',
    200: '#99CFFF',
    300: '#66B7FF',
    400: '#339FFF',
    500: '#1E90FF',
    600: '#0073E6',
    700: '#0056B3',
    800: '#003A80',
    900: '#001D4D',
    hover: '#3AA0FF',
    pressed: '#1470CC',
    light: '#E6F3FF',
    border: '#B3D9FF',
  },
  // Shell/Background Colors
  shell: {
    DEFAULT: '#0A1425',
    light: '#1A2435',
    lighter: '#2A3445',
  },
  // Status Colors
  success: {
    DEFAULT: '#2ECC71',
    50: '#E8F8F0',
    100: '#D1F2E1',
    500: '#2ECC71',
    600: '#27AE60',
    700: '#1E8449',
    light: '#D5F5E3',
  },
  warning: {
    DEFAULT: '#F39C12',
    50: '#FEF5E7',
    100: '#FDEBD0',
    500: '#F39C12',
    600: '#E67E22',
    700: '#CA6F1E',
    light: '#FCF3CF',
  },
  danger: {
    DEFAULT: '#E74C3C',
    50: '#FADBD8',
    100: '#F5B7B1',
    500: '#E74C3C',
    600: '#C0392B',
    700: '#922B21',
    light: '#FADBD8',
  },
  info: {
    DEFAULT: '#3498DB',
    50: '#D6EAF8',
    100: '#AED6F1',
    500: '#3498DB',
    600: '#2980B9',
    700: '#1F618D',
    light: '#D6EAF8',
  },
  // Neutral Grays
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
} as const
export const TYPOGRAPHY = {
  fontFamily: {
    sans: ['IBM Plex Sans', 'sans-serif'],
    mono: ['IBM Plex Mono', 'monospace'],
  },
  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    h1: '1.75rem', // 28px
    h2: '1.375rem', // 22px
    h3: '1.125rem', // 18px
    body: '1rem', // 16px
    small: '0.8125rem', // 13px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const
export const SPACING = {
  0: '0',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
} as const
export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  'level-1': '0px 1px 2px rgba(0, 0, 0, 0.05)',
  'level-2': '0px 4px 8px rgba(0, 0, 0, 0.08)',
  'level-3': '0px 8px 16px rgba(0, 0, 0, 0.12)',
} as const
export const BORDER_RADIUS = {
  none: '0',
  sm: '0.25rem', // 4px
  DEFAULT: '0.5rem', // 8px
  md: '0.5rem', // 8px
  lg: '0.75rem', // 12px
  xl: '1rem', // 16px
  '2xl': '1.5rem', // 24px
  full: '9999px',
} as const
export const TRANSITIONS = {
  duration: {
    instant: '100ms',
    fast: '150ms',
    base: '200ms',
    slow: '250ms',
    slower: '350ms',
  },
  timing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    decelerate: 'cubic-bezier(0.0, 0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
} as const
export const BREAKPOINTS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const
